import { useCallback, useLayoutEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DataTable } from '@/components/DataTable';
import { useMediaQuery, useShallowEqualSelector } from '@/hooks';
import { setSearchTerm } from '@/store/collaborators';

import {
	ButtonsCell,
	CreatedAtCell,
	EmailCell,
	FoldersCell,
	LastLoginCell,
	MobileView,
	RoleCell,
	StateCell
} from './components';

function Table({ data }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const searchTerm = useSelector(state => state.collaborators.searchTerm);
	const mdDown = useMediaQuery('mdDown');
	const [grouping, setGrouping] = useState([]);

	const { folders } = useShallowEqualSelector(({ myDocuments }) => ({
		folders: myDocuments.folders || []
	}));

	const ButtonsCellComponent = useCallback(
		row => <ButtonsCell row={row} />,
		[]
	);

	const FolderCellComponent = useCallback(
		row => <FoldersCell row={row} totalFolders={folders.length} />,
		[]
	);

	const MobileCellComponent = useCallback(
		row => <MobileView row={row} totalFolders={folders.length} />,
		[]
	);

	const columns = [
		{
			accessorKey: 'email',
			header: t('users.table.header.email'),
			cell: EmailCell
		},
		{
			accessorKey: 'role',
			header: t('users.table.header.role'),
			cell: RoleCell
		},
		{
			accessorKey: 'folderIds',
			header: t('users.table.header.permissions'),
			cell: ({ row }) => FolderCellComponent(row)
		},
		{
			accessorKey: 'createdAt',
			header: t('users.table.header.createdAt'),
			cell: CreatedAtCell
		},
		{
			accessorKey: 'lastLogin',
			header: t('users.table.header.lastLogin'),
			cell: LastLoginCell
		},
		{
			accessorKey: 'state',
			header: t('users.table.header.state'),
			cell: StateCell
		},
		{
			accessorKey: 'buttons',
			header: '',
			cell: ({ row }) => ButtonsCellComponent(row)
		},
		{
			id: 'data',
			accessorKey: 'data',
			aggregationFn: 'unique',
			header: '',
			cell: ({ row }) => MobileCellComponent(row)
		}
	];

	useLayoutEffect(() => {
		if (mdDown) {
			setGrouping([
				'email',
				'role',
				'folderIds',
				'createdAt',
				'lastLogin',
				'state',
				'buttons'
			]);
		} else {
			setGrouping(['data']);
		}
	}, [mdDown]);

	return (
		<DataTable
			className="mb-20"
			columns={columns}
			data={data || []}
			manualGrouping
			state={{
				searchTerm,
				grouping
			}}
			onGlobalFilterChange={value => dispatch(setSearchTerm(value))}
			onGroupingChange={setGrouping}
			tableCellClassName={mdDown ? 'hidden' : ''}
		/>
	);
}

export default Table;
